import { useEffect, useState, useCallback } from 'react'
import { useMaterialDetailLazyQuery } from '@src/queries/MaterialDetailQuery.generated'
import { useProductDetailLazyQuery } from '@src/queries/ProductDetailQuery.generated'
import { Suggestion } from '@src/types/graphql-types'
import { SearchFocusType } from '@src/utils/searchUtils'
import { HeaderSearchAddToCartProps } from './HeaderSearchAddToCart'
import { createPersistentStorage } from '@src/utils/createPersistentStorage'
import { useCurrentUser, SitePreference } from '@src/utils/useCurrentUser'
import { localStorage } from 'react-storage'
import { useMaterialCatalogFilter } from '@utils/useMaterialCatalogFilter'

const MIN_SEARCH_TERM_LENGTH = 5

interface HeaderSearchAddToCartStorage {
  headerSearchAddToCartUsed: boolean
}

export const headerSearchAddToCartStorage =
  createPersistentStorage<HeaderSearchAddToCartStorage>(
    localStorage,
    'HEADER_SEARCH_ADD_TO_CART',
    'v1'
  )

export const useHeaderSearchAddToCart = (
  inputValue: string | null | undefined,
  focusType: SearchFocusType | string,
  productQuickLook?: Suggestion | null
): HeaderSearchAddToCartProps => {
  const [getProductDetail, productDetailResults] = useProductDetailLazyQuery()
  const [getMaterialDetail, { variables, data }] = useMaterialDetailLazyQuery()
  const isProductSearch = focusType === SearchFocusType.Products
  const [quantity, setQuantity] = useState(1)
  const { getSitePreference } = useCurrentUser()
  const { catalogType, filter } = useMaterialCatalogFilter()
  const orgId = getSitePreference(SitePreference.CatalogFilter)

  useEffect(() => {
    if (
      (!isProductSearch ||
        !inputValue ||
        inputValue.trim().length < MIN_SEARCH_TERM_LENGTH) &&
      !productQuickLook
    ) {
      return
    }
    if (productQuickLook) {
      const { productKey, brandKey } = productQuickLook
      if (productKey && brandKey) {
        getProductDetail({
          variables: {
            productKey,
            brandKey,
            catalogType: null,
            orgId,
          },
        })
        return
      }
    }
    const materialNumber = inputValue?.trim()
    const variables = {
      materialNumber: materialNumber ? materialNumber : '',
      catalogType,
      filter,
    }
    getMaterialDetail({ variables })
  }, [isProductSearch, inputValue, getMaterialDetail, productQuickLook])

  const lastUsedInputValue = variables?.materialNumber
    ? variables.materialNumber
    : productDetailResults.variables?.productKey
      ? productDetailResults.variables.productKey
      : undefined

  return {
    material:
      isProductSearch && lastUsedInputValue === inputValue?.trim()
        ? data?.getMaterialDetail
        : undefined,
    product: productDetailResults.data?.getProductDetail,
    quantity,
    setQuantity,
    onAddToCartClick: useCallback(() => {
      headerSearchAddToCartStorage.set({
        headerSearchAddToCartUsed: true,
      })
    }, []),
  }
}
